import { ADMIN_CONFIG } from '@/constants/config';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  meta: { timestamp: string; path: string; statusCode: number };
}

interface AuthResponse {
  user: unknown;
  tokens: TokenPair;
}

class TokenManager {
  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  getTokens(): TokenPair | null {
    const accessToken = localStorage.getItem('admin_accessToken');
    const refreshToken = localStorage.getItem('admin_refreshToken');
    const expiresIn = localStorage.getItem('admin_expiresIn');
    const tokenType = localStorage.getItem('admin_tokenType');

    if (!accessToken || !refreshToken) {
      return null;
    }

    return {
      accessToken,
      refreshToken,
      expiresIn: expiresIn ? parseInt(expiresIn) : 0,
      tokenType: tokenType || 'Bearer',
    };
  }

  saveTokens(tokens: TokenPair): void {
    localStorage.setItem('admin_accessToken', tokens.accessToken);
    localStorage.setItem('admin_refreshToken', tokens.refreshToken);
    localStorage.setItem('admin_expiresIn', tokens.expiresIn.toString());
    localStorage.setItem('admin_tokenType', tokens.tokenType);
  }

  clearTokens(): void {
    localStorage.removeItem('admin_accessToken');
    localStorage.removeItem('admin_refreshToken');
    localStorage.removeItem('admin_expiresIn');
    localStorage.removeItem('admin_tokenType');
  }

  private async refreshAccessToken(): Promise<string> {
    const tokens = this.getTokens();
    if (!tokens?.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await axios.post<ApiResponse<AuthResponse>>(
        `${ADMIN_CONFIG.api.baseUrl}/auth/refresh`,
        { refreshToken: tokens.refreshToken },
        { headers: { 'Content-Type': 'application/json' } }
      );

      const newTokens = response.data.data.tokens;
      this.saveTokens(newTokens);
      return newTokens.accessToken;
    } catch {
      this.clearTokens();
      window.location.href = '/login';
      throw new Error('Token refresh failed');
    }
  }

  private subscribeTokenRefresh(callback: (token: string) => void): void {
    this.refreshSubscribers.push(callback);
  }

  private onTokenRefreshed(token: string): void {
    this.refreshSubscribers.forEach(callback => callback(token));
    this.refreshSubscribers = [];
  }

  setupInterceptors(axiosInstance: AxiosInstance): void {
    axiosInstance.interceptors.request.use(
      config => {
        const tokens = this.getTokens();
        if (tokens?.accessToken) {
          config.headers.Authorization = `${tokens.tokenType} ${tokens.accessToken}`;
        }
        config.headers['Accept-Language'] = 'fr';
        return config;
      },
      error => Promise.reject(error)
    );

    axiosInstance.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        if (
          error.response?.data?.statusCode === 400 &&
          ['auths.session_expired', 'auths.invalid_token'].includes(error.response?.data?.message) &&
          !originalRequest._retry
        ) {
          if (this.isRefreshing) {
            return new Promise(resolve => {
              this.subscribeTokenRefresh((token: string) => {
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${token}`;
                resolve(axiosInstance(originalRequest));
              });
            });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const newAccessToken = await this.refreshAccessToken();
            this.isRefreshing = false;
            this.onTokenRefreshed(newAccessToken);
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return axiosInstance(originalRequest);
          } catch (refreshError) {
            this.isRefreshing = false;
            return Promise.reject(refreshError);
          }
        }

        return Promise.reject(error);
      }
    );
  }
}

export const tokenManager = new TokenManager();
